import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

export default function FrequencyVisualModal({ frequencyHz, isOpen, onClose }) {
  const [visuals, setVisuals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (isOpen && frequencyHz) {
      loadVisuals();
    }
  }, [isOpen, frequencyHz]);

  const loadVisuals = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/frequency-visuals`, {
        params: { frequency_hz: frequencyHz }
      });
      setVisuals(res.data);
      setCurrentIndex(0);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load visuals");
    } finally {
      setLoading(false);
    }
  };

  const nextVisual = () => {
    setCurrentIndex((prev) => (prev + 1) % visuals.length);
  };

  const prevVisual = () => {
    setCurrentIndex((prev) => (prev - 1 + visuals.length) % visuals.length);
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#a4ff2d]">
            {frequencyHz} Hz Visuals
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center p-8">
            <p className="text-muted-foreground">Loading visuals...</p>
          </div>
        ) : visuals.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 space-y-4">
            <p className="text-muted-foreground">No visuals available for this frequency</p>
            <p className="text-xs text-muted-foreground">
              Visuals can be uploaded by admins at /admin/visuals
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="relative">
              {visuals[currentIndex].image_url && (
                <img
                  src={visuals[currentIndex].image_url}
                  alt={`${frequencyHz} Hz visual`}
                  className="w-full h-auto rounded-lg"
                />
              )}
              {visuals[currentIndex].video_url && (
                <video
                  src={visuals[currentIndex].video_url}
                  controls
                  loop
                  autoPlay
                  className="w-full h-auto rounded-lg"
                />
              )}
              {visuals[currentIndex].description && (
                <div className="mt-2 p-3 bg-black/60 rounded">
                  <p className="text-sm text-white">{visuals[currentIndex].description}</p>
                </div>
              )}
            </div>

            {visuals.length > 1 && (
              <div className="flex items-center justify-between">
                <Button onClick={prevVisual} variant="outline" size="sm">
                  ← Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  {currentIndex + 1} / {visuals.length}
                </span>
                <Button onClick={nextVisual} variant="outline" size="sm">
                  Next →
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
